import { useState } from "react";
import { toast } from "sonner";
import { Reveal } from "./Reveal";
import { api, formatApiError } from "../lib/api";
import { Phone, MessageCircle, Mail, MapPin, AtSign, Instagram, Send, Loader2 } from "lucide-react";

const CONTACTS = [
  { icon: Phone, label: "Telefono", value: "+39 329 633 0445", href: "tel:+393296330445", testid: "contact-phone-link" },
  { icon: MessageCircle, label: "WhatsApp", value: "+39 329 633 0445", href: "https://wa.me/393296330445", testid: "contact-whatsapp-link" },
  { icon: Mail, label: "Email", value: "valentina.maritato85@gmail.com", href: "mailto:valentina.maritato85@gmail.com", testid: "contact-email-link" },
  { icon: AtSign, label: "PEC", value: "valentina.maritato@pec.it", href: "mailto:valentina.maritato@pec.it", testid: "contact-pec-link" },
  { icon: Instagram, label: "Instagram", value: "@oltre_ilconflitto", href: "https://www.instagram.com/oltre_ilconflitto", testid: "contact-instagram-link" },
  { icon: MapPin, label: "Studio", value: "Via Claudio Crea, 25 — Crotone (KR)", href: "https://www.google.com/maps/search/Via+Claudio+Crea+25+Crotone", testid: "contact-address-link" },
];

const TYPES = ["Primo colloquio informativo", "Separazione / divorzio", "Gestione dei figli", "Costi e informazioni", "Altro"];

const EMPTY = { name: "", email: "", phone: "", request_type: TYPES[0], message: "", privacy: false };

export const ContactFormSection = () => {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);

  const set = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.privacy) {
      toast.error("Serve il consenso privacy per inviare la richiesta.");
      return;
    }
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success("Richiesta inviata. Ti ricontatterò al più presto, grazie.");
      setForm(EMPTY);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full rounded-xl border border-ink/15 bg-white px-4 py-3 text-sm sm:text-base text-ink placeholder:text-mist/60 focus-gold transition-colors";

  return (
    <section id="contatti" data-testid="contact-form-section" className="py-20 lg:py-32">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
        <Reveal className="lg:col-span-5">
          <p className="overline-tag mb-4">Contatti</p>
          <h2 className="font-serif font-semibold text-ink tracking-tight text-2xl sm:text-3xl lg:text-4xl leading-tight">
            Il primo passo è una conversazione.
          </h2>
          <p className="mt-5 text-base text-mist leading-relaxed max-w-md">
            Raccontami la tua situazione in poche righe: ti ricontatterò personalmente per fissare
            un colloquio informativo, in studio a Crotone o online. Tutto resta riservato.
          </p>

          <div className="mt-9 flex flex-col gap-3">
            {CONTACTS.map(({ icon: Icon, label, value, href, testid }) => (
              <a key={label} href={href} target={href.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer"
                data-testid={testid}
                className="group flex items-center gap-4 rounded-2xl border border-ink/10 bg-white px-5 py-4 hover:border-gold/60 hover:-translate-y-0.5 transition-all duration-300">
                <span className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-sand text-ink group-hover:bg-goldlight group-hover:text-golddeep transition-colors duration-300">
                  <Icon size={18} />
                </span>
                <span>
                  <span className="block font-display text-[11px] uppercase tracking-[0.18em] text-mist font-semibold">{label}</span>
                  <span className="block text-sm sm:text-base font-medium text-ink">{value}</span>
                </span>
              </a>
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.15} className="lg:col-span-7">
          <form onSubmit={submit} data-testid="contact-form"
            className="bg-white border border-ink/10 rounded-3xl p-6 sm:p-10 shadow-[0_30px_60px_-40px_rgba(20,41,46,0.35)]">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label htmlFor="cf-name" className="block text-sm font-semibold text-ink mb-2">Nome e cognome *</label>
                <input id="cf-name" data-testid="contact-form-name" required value={form.name} onChange={set("name")}
                  placeholder="Maria Rossi" className={inputCls} />
              </div>
              <div>
                <label htmlFor="cf-phone" className="block text-sm font-semibold text-ink mb-2">Telefono / WhatsApp</label>
                <input id="cf-phone" data-testid="contact-form-phone" value={form.phone} onChange={set("phone")}
                  placeholder="+39 ..." className={inputCls} />
              </div>
              <div>
                <label htmlFor="cf-email" className="block text-sm font-semibold text-ink mb-2">Email *</label>
                <input id="cf-email" type="email" data-testid="contact-form-email" required value={form.email} onChange={set("email")}
                  placeholder="maria@esempio.it" className={inputCls} />
              </div>
              <div>
                <label htmlFor="cf-type" className="block text-sm font-semibold text-ink mb-2">Tipo di richiesta</label>
                <select id="cf-type" data-testid="contact-form-type" value={form.request_type} onChange={set("request_type")} className={inputCls}>
                  {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
            </div>

            <div className="mt-5">
              <label htmlFor="cf-message" className="block text-sm font-semibold text-ink mb-2">Messaggio *</label>
              <textarea id="cf-message" data-testid="contact-form-message" required minLength={10} rows={5}
                value={form.message} onChange={set("message")}
                placeholder="Raccontami in poche righe la tua situazione…"
                className={`${inputCls} resize-y`} />
            </div>

            <label className="mt-5 flex items-start gap-3 cursor-pointer" data-testid="contact-form-privacy">
              <input type="checkbox" checked={form.privacy} onChange={set("privacy")}
                className="mt-1 w-4 h-4 rounded border-ink/30 accent-[#D99B26]" data-testid="contact-form-privacy-checkbox" />
              <span className="text-xs sm:text-sm text-mist leading-relaxed">
                Acconsento al trattamento dei miei dati personali per essere ricontattata/o,
                secondo la normativa privacy (GDPR). I dati non saranno mai ceduti a terzi.
              </span>
            </label>

            <button type="submit" disabled={sending} data-testid="contact-form-submit"
              className="mt-7 w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-full bg-ink text-paper font-semibold px-8 py-4 hover:bg-night hover:-translate-y-0.5 active:scale-95 disabled:opacity-60 disabled:hover:translate-y-0 transition-all duration-300">
              {sending ? <Loader2 size={17} className="animate-spin" /> : <Send size={16} />}
              {sending ? "Invio in corso…" : "Invia la richiesta"}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
